import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './MainScreens.css';

export default function InviteScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    loadReferralData();
  }, []);

  async function loadReferralData() {
    try {
      const { data } = await api.get('/referrals/my-code');
      setReferralData(data);
    } catch (err) {
      console.error('Failed to load referral data:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleCopyLink() {
    if (!referralData?.referral_link) return;
    navigator.clipboard.writeText(referralData.referral_link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = referralData.referral_link;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleCopyCode() {
    if (!referralData?.referral_code) return;
    navigator.clipboard.writeText(referralData.referral_code).then(() => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }).catch(() => {});
  }

  function handleShare() {
    if (!referralData) return;
    const text = `Stop arguing about where to eat! 🍕 Join Hangrily with my code ${referralData.referral_code}, visit a restaurant, and we both earn cashback! ${referralData.referral_link}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Join Hangrily',
        text,
        url: referralData.referral_link,
      }).catch(() => {});
    } else {
      handleCopyLink();
    }
  }

  if (loading) {
    return (
      <div className="main-screen">
        <MainHeader title="Invite Friends" showBack={true} backPath="/home" />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-screen">
      <MainHeader title="Invite Friends" showBack={true} backPath="/home" />

      <main className="screen-main">
        <div style={{ padding: '20px', maxWidth: '500px', margin: '0 auto' }}>

          {/* Hero Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #FF6347 0%, #FF8C00 100%)',
            padding: '28px 24px',
            borderRadius: '16px',
            textAlign: 'center',
            marginBottom: '24px',
            color: '#fff',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎁</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '800' }}>
              Give $1, Get $2
            </h2>
            <p style={{ margin: 0, fontSize: '14px', opacity: 0.95, lineHeight: '1.5' }}>
              Invite friends to Hangrily. When they visit a restaurant and verify it, 
              you get <strong>$2.00</strong> cashback and they get <strong>$1.00</strong>!
            </p>
          </div>

          {/* Referral Code Card */}
          {referralData && (
            <div style={{
              background: '#1e1e3a',
              border: '1px solid #333',
              borderRadius: '14px',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div style={{ color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                Your Referral Code
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  flex: 1,
                  background: '#0d0d1a',
                  border: '2px dashed #FF6347',
                  borderRadius: '10px',
                  padding: '14px',
                  textAlign: 'center',
                  fontSize: '24px',
                  fontWeight: '800',
                  color: '#FF6347',
                  letterSpacing: '4px',
                }}>
                  {referralData.referral_code}
                </div>
                <button
                  onClick={handleCopyCode}
                  style={{
                    background: '#333',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '14px',
                    cursor: 'pointer',
                    fontSize: '18px',
                  }}
                >
                  {copiedCode ? '✅' : '📋'}
                </button>
              </div>

              {/* Share Buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleShare}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'linear-gradient(135deg, #FF6347, #FF8C00)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  📤 Share Invite
                </button>
                <button
                  onClick={handleCopyLink}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  {copied ? '✅ Copied!' : '🔗 Copy Link'}
                </button>
              </div>
            </div>
          )}

          {/* Stats Card */}
          {referralData?.stats && (
            <div style={{
              background: '#1e1e3a',
              border: '1px solid #333',
              borderRadius: '14px',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <h3 style={{ color: '#fff', fontSize: '16px', margin: '0 0 16px 0', fontWeight: '700' }}>
                Your Referral Stats
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#FF6347' }}>
                    {referralData.stats.total_invited}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Invited</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#06C167' }}>
                    {referralData.stats.converted}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Joined</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#FFD43B' }}>
                    ${(referralData.stats.total_earned_cents / 100).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Earned</div>
                </div>
              </div>
            </div>
          )}

          {/* How It Works */}
          <div style={{
            background: '#1a1a2e',
            border: '1px solid #222',
            borderRadius: '14px',
            padding: '20px',
            marginBottom: '16px',
          }}>
            <h3 style={{ color: '#fff', fontSize: '16px', margin: '0 0 16px 0', fontWeight: '700' }}>
              How It Works
            </h3>
            {[
              { step: '1', emoji: '📤', title: 'Share your code', desc: 'Send your unique link or code to friends' },
              { step: '2', emoji: '📱', title: 'Friend signs up', desc: 'They create a Premium account using your code' },
              { step: '3', emoji: '🍽️', title: 'Friend visits a restaurant', desc: 'They search, pick a spot, go eat, and submit their receipt' },
              { step: '4', emoji: '💰', title: 'You both earn', desc: 'Once the visit is verified, you get $2.00 and they get $1.00!' },
            ].map((item) => (
              <div key={item.step} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                marginBottom: '14px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#2a2a4a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0,
                }}>
                  {item.emoji}
                </div>
                <div>
                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>{item.title}</div>
                  <div style={{ color: '#888', fontSize: '12px' }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Terms */}
          <div style={{
            padding: '12px 16px',
            background: '#111',
            borderRadius: '10px',
            marginBottom: '16px',
          }}>
            <p style={{ color: '#666', fontSize: '11px', margin: 0, lineHeight: '1.6' }}>
              Referral bonuses are credited as cashback. Referrer earns $2.00 per successful referral. 
              Friend must sign up for Premium, search for restaurants, visit one, and submit a verified receipt 
              before bonuses are awarded. Verification requires a receipt photo, bill amount ($10+), and location. 
              Cashback redeemable for gift cards or subscription credit. Fraudulent or duplicate accounts will be disqualified.
            </p>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
